import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Badge, CallStateBadge, Card, cx } from '@/components/ui';
import { IconBot, IconMusic, IconTransfer, IconWaves } from '@/components/icons';
import { formatPhone } from '@/lib/format';
import { useMyLicense } from '@/lib/license';

interface LiveCall {
  id: string;
  state: string;
  caller_number: string | null;
  started_at: string;
  bot_name: string | null;
  bot_kind: 'AI' | 'SOUNDBOARD' | null;
}

const REFRESH_MS = 2_000;

/**
 * What is happening on the account this second.
 *
 * Polled every two seconds rather than pushed: a dashboard left open on a
 * wall screen must not depend on a websocket staying up through a night of
 * proxies, and the query behind it is one indexed read of open rows.
 */
export function LiveNow() {
  const { data } = useQuery({
    queryKey: ['live-now'],
    queryFn: () => api.get<{ data: LiveCall[] }>('/calls/live'),
    refetchInterval: REFRESH_MS,
    placeholderData: (previous) => previous,
  });
  const { data: license } = useMyLicense();
  const now = useTicker(1_000);

  const calls = data?.data ?? [];
  const ai = calls.filter((c) => (c.bot_kind ?? 'AI') === 'AI').length;
  const boards = calls.filter((c) => c.bot_kind === 'SOUNDBOARD').length;
  const transferring = calls.filter((c) =>
    ['TRANSFER_REQUESTED', 'TRANSFER_RINGING', 'HUMAN_CONNECTED'].includes(c.state),
  ).length;
  const channels = (kind: 'AI' | 'SOUNDBOARD') =>
    license?.enforced ? license.engines.find((e) => e.kind === kind)?.channels ?? 0 : null;

  const tiles = [
    { label: 'Bots on calls', value: calls.length, sub: 'right now', icon: <IconWaves />, tint: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-400/10 dark:text-emerald-300' },
    { label: 'AI Bot', value: ai, sub: channels('AI') !== null ? `of ${channels('AI')} channels` : 'calls', icon: <IconBot />, tint: 'text-blue-600 bg-blue-50 dark:bg-blue-400/10 dark:text-blue-300' },
    { label: 'Soundboard', value: boards, sub: channels('SOUNDBOARD') !== null ? `of ${channels('SOUNDBOARD')} channels` : 'calls', icon: <IconMusic />, tint: 'text-violet-600 bg-violet-50 dark:bg-violet-400/10 dark:text-violet-300' },
    { label: 'Transferring', value: transferring, sub: 'to a closer', icon: <IconTransfer />, tint: 'text-amber-600 bg-amber-50 dark:bg-amber-400/10 dark:text-amber-300' },
  ];

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          Live now
        </span>
      }
      description="Refreshes every 2 seconds"
      actions={
        <Link to="/calls/live" className="text-xs font-semibold text-brand-600 hover:underline dark:text-brand-400">
          Open live board
        </Link>
      }
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((tile) => (
          <div key={tile.label} className="flex items-center gap-3 rounded-lg border border-line bg-sunken/40 px-3 py-2.5">
            <span className={cx('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[17px]', tile.tint)}>
              {tile.icon}
            </span>
            <div className="min-w-0">
              <p className="text-xl font-bold tabular-nums leading-tight text-ink-900 dark:text-ink-50">{tile.value}</p>
              <p className="truncate text-xs font-semibold text-ink-700 dark:text-ink-200">{tile.label}</p>
              <p className="truncate text-2xs text-ink-500 dark:text-ink-400">{tile.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {calls.length > 0 && (
        <ul className="mt-4 divide-y divide-line rounded-lg border border-line">
          {calls.slice(0, 8).map((call) => (
            <li key={call.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
              <Badge tone={call.bot_kind === 'SOUNDBOARD' ? 'purple' : 'blue'}>
                {call.bot_kind === 'SOUNDBOARD' ? 'Soundboard' : 'AI Bot'}
              </Badge>
              <span className="min-w-0 flex-1 truncate font-medium text-ink-800 dark:text-ink-100">
                {call.bot_name ?? 'Bot'}
              </span>
              <span className="font-mono text-xs text-ink-500">{formatPhone(call.caller_number)}</span>
              <CallStateBadge state={call.state} />
              <span className="w-12 text-right font-mono text-xs tabular-nums text-ink-600 dark:text-ink-300">
                {elapsed(call.started_at, now)}
              </span>
            </li>
          ))}
          {calls.length > 8 && (
            <li className="px-3 py-2 text-xs text-ink-500">and {calls.length - 8} more on the live board</li>
          )}
        </ul>
      )}
    </Card>
  );
}

function useTicker(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

function elapsed(startedAt: string, now: number): string {
  const seconds = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
